<template>
  <!-- Green Screen top bar for phones: the desktop bar's tiles in one row that scrolls sideways.
       A tile with options opens them in a second row underneath; tapping it again, or anywhere
       outside the bar, closes the row. -->
  <div ref="root" class="green-screen-bar fixed inset-x-0 top-0 z-[500] flex flex-col bg-layer-2" data-testid="green-screen-bar-mobile">
    <div class="green-screen-bar__row flex items-center gap-2 h-[82px] px-4 py-[10px] overflow-x-auto">
      <button :class="[tileClass, { [tilePressedClass]: activeMenu === 'views' }]" :aria-expanded="activeMenu === 'views'"
        data-testid="views-menu-button" @click="toggleMenu('views')">
        <dynamic-svg class="w-6 h-6" src="/svg/stand.svg" />
        <span>{{ $t('session_views') }}</span>
      </button>

      <button :class="[tileClass, { [tilePressedClass]: activeMenu === 'size' }]" :aria-expanded="activeMenu === 'size'"
        data-testid="font-size-menu-button" @click="toggleMenu('size')">
        <img class="w-6 h-6" src="@/assets/icons/sorenson/font-size.svg" alt="" />
        <span>{{ sizeLabel }}</span>
      </button>

      <button :class="tileClass" data-testid="line-count-toggle" :data-line-count="lines"
        :aria-label="`${lineCountLabel}: ${lines}`" @click="emit('lines', nextLineCount(lines))">
        <span class="h-6 text-[20px] leading-6 font-bold">{{ lines }}</span>
        <span>{{ lineCountLabel }}</span>
      </button>

      <button v-for="menu in choiceMenus" :key="menu.key"
        :class="[tileClass, { [tilePressedClass]: activeMenu === menu.key }]" :aria-expanded="activeMenu === menu.key"
        :data-testid="`${menu.key}-menu-button`" @click="toggleMenu(menu.key)">
        <img class="w-6 h-6" :src="menu.icon" alt="" />
        <span>{{ menu.label }}</span>
      </button>

      <button :class="[tileClass, { [tilePressedClass]: activeMenu === 'background' }]" :aria-expanded="activeMenu === 'background'"
        data-testid="background-color-picker" @click="toggleMenu('background')">
        <img class="w-6 h-6" src="@/assets/icons/palette.svg" alt="" />
        <span>{{ backgroundLabel }}</span>
      </button>

      <button class="top-bar-button top-bar-button--leave flex-none min-w-[64px] h-[58px] bg-button-secondary-destructive-resting hover:bg-button-secondary-destructive-active" @click="emit('leave')">
        <img src="@/assets/icons/sorenson/align-right-01.svg" alt="Leave">
        <span class="text-text-neutral-red">{{ $t('session_leave') }}</span>
      </button>
    </div>

    <div
        v-if="activeMenu"
        class="green-screen-bar__row flex items-center gap-2 min-h-[62px] px-4 py-2 bg-layer-1 overflow-x-auto whitespace-nowrap"
        :data-testid="`green-screen-options-${activeMenu}`"
    >
      <span class="shrink-0 pr-1 text-[14px] leading-[20px] font-medium text-text-neutral-high">{{ activeMenuLabel }}:</span>

      <template v-if="activeMenu === 'views'">
        <button v-for="item in views" :key="item.code" :class="chipClass(item.code === 'chromakey')"
          :data-testid="`view-option-${item.code}`" @click="selectView(item.code)">
          <dynamic-svg :src="item.icon" width="16" height="16" fill="currentColor" fill-opacity="1" />
          {{ $t(item.label) }}
        </button>
      </template>

      <template v-else-if="activeMenu === 'size'">
        <div class="flex flex-row shrink-0 rounded-[10px] overflow-hidden">
          <button :disabled="fontSize <= minFontSize" class="bg-button-secondary-mono-resting h-10 w-10 flex justify-center items-center cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-decrease" aria-label="Decrease Font Size" @click="emit('step-font-size', -1)">
            <img width="24" height="24" src="@/assets/icons/minus.svg" alt="-" />
          </button>
          <button :disabled="fontSize >= maxFontSize" class="bg-button-secondary-mono-resting h-10 w-10 flex justify-center items-center border-l-[3px] border-layer-1 cursor-pointer disabled:opacity-50 disabled:cursor-default"
            data-testid="font-increase" aria-label="Increase Font Size" @click="emit('step-font-size', 1)">
            <img width="24" height="24" src="@/assets/icons/plus.svg" alt="+" />
          </button>
        </div>
        <input
            v-model="fontSizeDraft"
            type="number"
            inputmode="numeric"
            :min="minFontSize"
            :max="maxFontSize"
            step="1"
            :aria-label="sizeLabel"
            class="font-size-input shrink-0 border-2 border-[#292F36] rounded-[10px] bg-transparent text-text-neutral-extreme font-normal text-[16px] h-10 w-[64px] px-[10px] outline-none focus:border-[#6CBFFF]"
            data-testid="font-size-input"
            @change="commitFontSize"
            @keydown.enter="$event.target.blur()"
        />
      </template>

      <template v-else-if="activeMenu === 'background'">
        <slot name="background"></slot>
      </template>

      <div v-else-if="activeChoice" class="flex flex-row gap-2" role="radiogroup" :aria-label="activeChoice.label">
        <button
            v-for="option in activeChoice.options"
            :key="option.value"
            role="radio"
            :aria-checked="activeChoice.value === option.value"
            :class="chipClass(activeChoice.value === option.value)"
            :style="option.css ? { fontFamily: option.css } : null"
            :data-testid="`${activeChoice.key}-option-${option.value}`"
            @click="emit(activeChoice.key, option.value)"
        >
          <svg v-if="activeChoice.value === option.value" class="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" :d="CHECK_ICON_PATH" fill="currentColor" />
          </svg>
          <img v-if="activeChoice.optionIcons" class="w-5 h-5 shrink-0" :src="option.icon" alt="" />
          {{ option.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import DynamicSvg from '@/components/ui/DynamicSvg.vue';
import { useFontSizeDraft } from '@/composables/useCaptionSettings';
import {
  CHECK_ICON_PATH,
  GREEN_SCREEN_BAR_EMITS,
  GREEN_SCREEN_BAR_PROPS,
  GREEN_SCREEN_VIEWS as views,
  nextLineCount,
  useGreenScreenBar,
} from '@/composables/useGreenScreenBar';
import { i18n } from '@/plugins/i18n';

const props = defineProps(GREEN_SCREEN_BAR_PROPS)

const emit = defineEmits(GREEN_SCREEN_BAR_EMITS)

const tileClass = 'flex flex-none flex-col items-center justify-center gap-1 min-w-[64px] h-[58px] px-2 rounded-[12px] text-xs font-medium whitespace-nowrap text-text-neutral-high bg-button-secondary-mono-resting cursor-pointer outline-none'
const tilePressedClass = '!bg-button-secondary-mono-active'
const chipClass = (selected) => [
  'flex shrink-0 items-center gap-1.5 h-10 px-4 rounded-[10px] text-[16px] leading-[20px] text-text-neutral-high cursor-pointer',
  selected ? 'bg-button-secondary-mono-active' : 'bg-button-secondary-mono-resting',
]

const { sizeLabel, lineCountLabel, backgroundLabel, choiceMenus } = useGreenScreenBar(props, { showWidth: false })

// 'views' | 'size' | 'background' | a choice menu key | null
const activeMenu = ref(null)

const toggleMenu = (key) => {
  activeMenu.value = activeMenu.value === key ? null : key
}

const activeChoice = computed(() => choiceMenus.value.find((menu) => menu.key === activeMenu.value) || null)

const activeMenuLabel = computed(() => {
  switch (activeMenu.value) {
    case 'views': return i18n.global.t('session_views')
    case 'size': return sizeLabel
    case 'background': return backgroundLabel.value
    default: return activeChoice.value ? activeChoice.value.label : ''
  }
})

const selectView = (code) => {
  activeMenu.value = null
  emit('view', code)
}

// Keeps the bar from sliding away while the second row is open.
watch(() => activeMenu.value !== null, (open) => emit('menu-state', open))

// Tapping outside the bar (the captions, the background) closes the second row.
const root = ref(null)
const closeOnOutsidePointer = (event) => {
  if (activeMenu.value && root.value && !root.value.contains(event.target)) {
    activeMenu.value = null
  }
}
onMounted(() => document.addEventListener('pointerdown', closeOnOutsidePointer))
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeOnOutsidePointer)
  if (activeMenu.value) {
    emit('menu-state', false)
  }
})

const { fontSizeDraft, commitFontSize } = useFontSizeDraft(props, emit)
</script>

<style scoped>
.green-screen-bar__row {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.green-screen-bar__row::-webkit-scrollbar {
  display: none;
}

.font-size-input {
  -moz-appearance: textfield;
  appearance: textfield;
  text-align: center;
}

.font-size-input::-webkit-outer-spin-button,
.font-size-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
